import { FC, Fragment } from 'react';

import MailStepBadge from './MailStepBadge';

export type MailAlertSegment =
  | { type: 'text'; value: string }
  | { type: 'mail'; stepIndex: number };

export type MailAlertItem = {
  key: string;
  /** Bullet body; may include Mail step badges. */
  segments: MailAlertSegment[];
};

export const textSegment = (value: string): MailAlertSegment => ({
  type: 'text',
  value,
});

export const mailSegment = (stepIndex: number): MailAlertSegment => ({
  type: 'mail',
  stepIndex,
});

export const segmentsToPlainText = (segments: MailAlertSegment[]): string =>
  segments
    .map((segment) =>
      segment.type === 'mail'
        ? `Mail #${segment.stepIndex + 1}`
        : segment.value,
    )
    .join('');

/** Parse server errors like "Mail 2: Activity 1 needs …" into badge + text segments. */
export const parseMailPrefixedError = (message: string): MailAlertSegment[] => {
  const match = message.match(/^Mail\s+(\d+)\s*:\s*(.*)$/s);
  if (!match) {
    return [textSegment(message)];
  }
  const stepNumber = Number(match[1]);
  const rest = match[2]?.trim() ?? '';
  if (!Number.isFinite(stepNumber) || stepNumber < 1) {
    return [textSegment(message)];
  }
  return rest
    ? [mailSegment(stepNumber - 1), textSegment(`: ${rest}`)]
    : [mailSegment(stepNumber - 1)];
};

type Props = {
  segments: MailAlertSegment[];
};

const MailAlertMessage: FC<Props> = ({ segments }) => (
  <>
    {segments.map((segment, index) =>
      segment.type === 'mail' ? (
        <MailStepBadge
          key={`mail-${segment.stepIndex}-${index}`}
          stepIndex={segment.stepIndex}
        />
      ) : (
        <Fragment key={`text-${index}`}>{segment.value}</Fragment>
      ),
    )}
  </>
);

export default MailAlertMessage;
